import axios from "axios";
import { computeGeometryCentroid } from "./centroid";
import { GeoJsonFeature, GeoJsonType, Geolevel } from "../map3d/typed";

import { assetUrl } from "../utils/assetUrl";

const GEO_ROOT = assetUrl("/china_geo_data");

/** 行政区更名别名（现用名 → 本地 china_geo_data 旧名） */
const REGION_ALIASES: Record<string, string[]> = {
  建安区: ["许昌县"],
  淮阳区: ["淮阳县"],
};

/** 区/县/市更名不一致时，生成候选名称 */
export function expandRegionNameVariants(name: string): string[] {
  const names = new Set<string>([name]);
  const suffixes = [
    "自治县",
    "自治旗",
    "林区",
    "特区",
    "新区",
    "矿区",
    "区",
    "县",
    "市",
    "旗",
  ];

  let core = name;
  for (const suffix of suffixes) {
    if (name.endsWith(suffix) && name.length > suffix.length) {
      core = name.slice(0, -suffix.length);
      break;
    }
  }

  names.add(core);
  ["区", "县", "市", "旗"].forEach((suffix) => {
    names.add(`${core}${suffix}`);
  });

  (REGION_ALIASES[name] || []).forEach((alias) => {
    names.add(alias);
  });

  return Array.from(names);
}

function encodePath(segments: string[]) {
  return segments.map(encodeURIComponent).join("/");
}

function toPublicUrl(relativePath: string) {
  return `${GEO_ROOT}/${encodePath(relativePath.split("/"))}`;
}

/** 根据面包屑路径，解析乡镇数据文件候选路径 */
export function resolveTownshipCandidates(pathNames: string[]): string[] {
  const candidates: string[] = [];
  const seen = new Set<string>();

  const push = (relativePath: string) => {
    if (!seen.has(relativePath)) {
      seen.add(relativePath);
      candidates.push(relativePath);
    }
  };

  if (pathNames.length === 2) {
    const [province, region] = pathNames;
    expandRegionNameVariants(region).forEach((regionName) => {
      // 直辖市：北京市/北京市/geo_海淀区.json
      push(`${province}/${province}/geo_${regionName}.json`);
      // 直筒子市：广东省/东莞市/geo_东莞市.json
      push(`${province}/${regionName}/geo_${regionName}.json`);
    });
  }

  // 区县级：省/市/区 → 区下全部乡镇
  if (pathNames.length === 3) {
    const [province, city, district] = pathNames;
    expandRegionNameVariants(district).forEach((districtName) => {
      push(`${province}/${city}/geo_${districtName}.json`);
    });
  }

  return candidates;
}

/** 单个乡镇：省/市/区/乡镇 → 目录内 geo_乡镇.json */
export function resolveTownCandidates(pathNames: string[]): string[] {
  if (pathNames.length < 4) {
    return [];
  }

  const [province, city, district, town] = pathNames;
  const candidates: string[] = [];
  const seen = new Set<string>();

  const push = (relativePath: string) => {
    if (!seen.has(relativePath)) {
      seen.add(relativePath);
      candidates.push(relativePath);
    }
  };

  expandRegionNameVariants(district).forEach((districtName) => {
    push(`${province}/${city}/${districtName}/geo_${town}.json`);
  });

  return candidates;
}

function syntheticAdcode(name: string, index: number) {
  let hash = 0;
  const text = `${name}-${index}`;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return 900000000 + (hash % 100000000);
}

function normalizeFeature(
  raw: any,
  index: number,
  level: Geolevel = "street"
): GeoJsonFeature | null {
  if (!raw?.geometry) {
    return null;
  }

  const props = raw.properties || {};
  const name = props.乡 || props.name || props.县 || `区域${index + 1}`;
  const centroid =
    props.centroid ||
    props.center ||
    computeGeometryCentroid(raw.geometry) ||
    ([0, 0] as [number, number]);

  return {
    type: "Feature",
    properties: {
      adcode: props.adcode ?? syntheticAdcode(name, index),
      name,
      center: centroid,
      centroid,
      childrenNum: level === "town" ? 0 : props.childrenNum ?? 0,
      level,
      parent: {
        adcode: props.parent?.adcode ?? 0,
      },
      subFeatureIndex: index,
      acroutes: [],
      adchar: null,
    },
    geometry: raw.geometry,
    vector3: [],
  } as GeoJsonFeature;
}

function normalizeToFeatureCollection(
  raw: any,
  level: Geolevel = "street"
): GeoJsonType {
  let list: any[] = [];

  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw?.type === "FeatureCollection") {
    list = raw.features || [];
  } else if (raw?.type === "Feature") {
    list = [raw];
  }

  const features = list
    .map((item, index) => normalizeFeature(item, index, level))
    .filter(Boolean) as GeoJsonFeature[];

  return {
    type: "FeatureCollection",
    features,
  };
}

async function tryFetchJson(url: string) {
  const response = await axios.get(url, {
    validateStatus: (status) => status >= 200 && status < 300,
    responseType: "json",
  });
  return response.data;
}

function isFeatureCollection(data: any): data is GeoJsonType {
  return (
    !!data &&
    typeof data === "object" &&
    data.type === "FeatureCollection" &&
    Array.isArray(data.features)
  );
}

function isFeature(data: any): boolean {
  return !!data && typeof data === "object" && data.type === "Feature";
}

/** 本地单体边界：public/geo/{adcode}.json */
function localMetaUrls(adcode: number): string[] {
  return [assetUrl(`/geo/${adcode}.json`)];
}

/** 加载区县下的乡镇 GeoJSON（转为项目统一 FeatureCollection） */
export async function loadTownshipGeoJson(
  pathNames: string[]
): Promise<GeoJsonType> {
  const candidates = resolveTownshipCandidates(pathNames);
  if (candidates.length === 0) {
    throw new Error(`无法解析乡镇路径: ${pathNames.join("/")}`);
  }

  const errors: string[] = [];

  for (const relativePath of candidates) {
    const url = toPublicUrl(relativePath);
    try {
      const raw = await tryFetchJson(url);
      const geoJson = normalizeToFeatureCollection(raw, "street");
      if (geoJson.features.length === 0) {
        errors.push(`${url} 无要素`);
        continue;
      }
      return geoJson;
    } catch (error: any) {
      errors.push(`${url}: ${error?.message || error}`);
    }
  }

  throw new Error(
    `未找到乡镇数据（${pathNames.join("/")}）\n` +
      `请确认已克隆 data/china_geo_data，并执行 npm run link:township\n` +
      `${errors.join("\n")}`
  );
}

/** 加载单个乡镇轮廓 */
export async function loadTownGeoJson(
  pathNames: string[]
): Promise<GeoJsonType> {
  const candidates = resolveTownCandidates(pathNames);
  if (candidates.length === 0) {
    throw new Error(`无法解析乡镇路径: ${pathNames.join("/")}`);
  }

  const errors: string[] = [];

  for (const relativePath of candidates) {
    const url = toPublicUrl(relativePath);
    try {
      const raw = await tryFetchJson(url);
      const geoJson = normalizeToFeatureCollection(raw, "town");
      if (geoJson.features.length === 0) {
        errors.push(`${url} 无要素`);
        continue;
      }
      return geoJson;
    } catch (error: any) {
      errors.push(`${url}: ${error?.message || error}`);
    }
  }

  throw new Error(
    `未找到乡镇数据（${pathNames.join("/")}）\n${errors.join("\n")}`
  );
}

/** 从区县乡镇列表中切出单个乡镇（离线兜底，无需再请求） */
export function sliceTownFeature(
  geoJson: GeoJsonType,
  townName: string
): GeoJsonType | null {
  const feature = geoJson.features.find(
    (item) => item.properties?.name === townName
  );
  if (!feature) {
    return null;
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        ...feature,
        properties: {
          ...feature.properties,
          level: "town",
          childrenNum: 0,
        },
      },
    ],
  };
}

/** 加载本地行政边界：public/geo/{adcode}_full.json */
export async function loadLocalGeoJson(adcode: number): Promise<GeoJsonType> {
  const url = assetUrl(`/geo/${adcode}_full.json`);
  try {
    const data = await tryFetchJson(url);
    if (isFeatureCollection(data)) {
      return data;
    }
    throw new Error(`${url}: 非 FeatureCollection`);
  } catch (error: any) {
    throw new Error(
      `本地缺少地图数据：/geo/${adcode}_full.json\n` +
        `请先执行 npm run download:geo\n` +
        `${error?.message || error}`
    );
  }
}

/** 从本地 GeoJSON 中查找区域要素 */
export async function findRegionFeature(
  adcode: number
): Promise<GeoJsonType["features"][number] | null> {
  const china = await loadLocalGeoJson(100000);
  const hit = china.features.find((f) => Number(f.properties.adcode) === adcode);
  if (hit) {
    return hit;
  }

  // 市级等：本地单体 geo/{adcode}.json
  for (const url of localMetaUrls(adcode)) {
    try {
      const data = await tryFetchJson(url);
      if (isFeatureCollection(data)) {
        return data.features?.[0] ?? null;
      }
      if (isFeature(data)) {
        return data as GeoJsonType["features"][number];
      }
    } catch {
      // continue
    }
  }
  return null;
}
